import { useDispatch } from "react-redux";
import { useState, useMemo } from "react";
import usePostData from "../../../../../CustomHooks/useAxios/usePostData";
import { START_JOB_CARD } from "../../../../../api/allApi_List";
import ErrorDisplay from "../../../../../components/UI/ErrorDisplay";
import { setJobCardData } from "../../../../../redux/jobCardDataSlice";
import { isHydroExpired } from "../../../config/isHydroExpired";
import sortDataByDate from "../../../../../functions/sorting/sortDataByDate";
import { formatDate } from "../../../../../functions/dateTime/formatDate";

function CylinderSelection({ cylinderSetup, setCurrentStep }) {
  const [selectedCy, setSelectedCy] = useState(null);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const { executePost, loading, formError, setFormError } = usePostData();
  // sort data by cylinder updated at date
  const sortedCylinderSetup = sortDataByDate(cylinderSetup, "updatedAt");

  const updateStateOnResponse = (data) => {
    dispatch(setJobCardData(data));
    setCurrentStep(data.stageId);
  };

  const sendData = async (e) => {
    e.stopPropagation();
    if (!selectedCy) return;
    executePost({}, START_JOB_CARD + selectedCy, updateStateOnResponse);
  };
  // filter cylinder based on cylinder number and size
  const processedCylinders = useMemo(() => {
    const query = search.toLowerCase();

    return sortedCylinderSetup.filter((cyl) => {
      return (
        cyl.serialNumber.toLowerCase().includes(query) ||
        String(cyl.size).includes(query)
      );
    });
  }, [search, sortedCylinderSetup]);

  return (
    <div onClick={() => setSelectedCy(null)}>
      {formError && (
        <ErrorDisplay
          message={formError}
          closeAction={() => setFormError("")}
        />
      )}
      <h3 className="form-step-title">Cylinder Arrived In Factory</h3>
      {/* Cylinder header with search */}
      <div className="border-1 border-gray-200 rounded-md p-2 md:p-4 bg-gray-50 mt-3 md:mt-6">
        <div className="flex flex-col sm:flex-row gap-2 pb-2 items-center justify-between">
          <p className="text-center text-gray-500 max-md:text-sm">
            Select Cylinder to start the job card
          </p>
          {/* Search */}
          <input
            type="text"
            placeholder="Search by cylinder no or size (kg)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-blue-200 rounded-md p-1 md:p-2 text-xs md:text-sm  w-full sm:w-72 focus-input"
          />
        </div>
        {/* Cylinder Cards */}
        <div className="h-[calc(100vh-20em)] md:h-[calc(100vh-23em)] overflow-auto">
          {processedCylinders.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
              {processedCylinders.map((cyl) => {
                const isSelected = selectedCy === cyl.id;
                const cardClass = `relative text-left p-2 md:p-4 rounded-lg border transition-all ${
                  isSelected
                    ? "border-blue-400 bg-blue-100 shadow-lg"
                    : "border-gray-300 hover:border-blue-400 hover:bg-blue-100 bg-white"
                }
`;
                const hydroExpired = isHydroExpired(cyl.lastHydroTestingDate);
                return (
                  <button
                    key={cyl.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCy(cyl.id);
                    }}
                    className={cardClass}
                  >
                    {/* Cylinder No */}
                    <p className="md:text-lg font-figtreeMedium">
                      {cyl.serialNumber}
                      {cyl.station.name !== "Factory Station" && (
                        <span className="text-[13px] text-yellow-600">
                          {" "}
                          (On Site)
                        </span>
                      )}
                    </p>

                    {/* Meta Info */}
                    <div className="md:mt-2 space-y-1 text-xs md:text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Weight:</span> {cyl.size}{" "}
                        kg
                      </p>

                      <p
                        className={
                          hydroExpired ? "text-red-600" : "text-gray-600"
                        }
                      >
                        <span className="font-medium">Last Hydro:</span>{" "}
                        {formatDate(cyl.lastHydroTestingDate) || "N/A"}
                      </p>
                    </div>

                    {/* Status Badge */}
                    <span
                      className={`
                  absolute top-3 right-3 px-2 py-1 text-[10px] md:text-xs rounded-full
                  ${
                    cyl.currentState.isFilled
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-gray-100 text-gray-700"
                  }
                `}
                    >
                      {cyl.currentState.isFilled ? "Filled" : "Empty"}
                    </span>
                    {hydroExpired && (
                      <span className="absolute bottom-3 right-3 px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                        Hydro Expired
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-600 py-6">No Cylinder Found</p>
          )}
        </div>
      </div>
      {/* Action Button */}
      <div className="flex justify-end pt-6">
        <button
          disabled={!selectedCy || loading}
          onClick={sendData}
          className={`
            bg-blue-100 text-blue-700 border-blue-300 px-2 md:px-4 py-1 md:py-2 rounded-md text-sm font-medium border hover:bg-sky-50
            ${(!selectedCy || loading) && "opacity-50"}
          `}
        >
          {loading ? "Starting..." : "Start Job Card"}
        </button>
      </div>
    </div>
  );
}

export default CylinderSelection;
